import React, {FunctionComponent, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
    Box,
    IconButton,
    InputAdornment,
    Popover,
    PopoverActions,
    Tab,
    Tabs,
    TextField,
    Typography,
} from '@mui/material';
import {ClearRounded, SearchRounded} from '@mui/icons-material';
import {useTranslation} from 'react-i18next';
import {useFixedRowVirtualizer} from '../../../../hooks/use-row-virtualizer';
import VideoRow, {TutorialVideo, VIDEO_ROW_HEIGHT} from './video-row';

const POPOVER_WIDTH = 380;
const LIST_MAX_HEIGHT = 380;
const VIRTUALIZE_THRESHOLD = 8;

type TutorialsTab = 'applicable' | 'all';

interface TutorialsPopoverProps {
    open: boolean;
    anchorEl: Element | null | undefined;
    onClose: () => void;
    popoverAction: React.Ref<PopoverActions>;
    applicableTutorials: TutorialVideo[];
    allTutorials: TutorialVideo[];
}

/** Biz Learn videos popover with Applicable/All tabs, search, and virtualized compact rows. */
const TutorialsPopover: FunctionComponent<TutorialsPopoverProps> = ({
    open,
    anchorEl,
    onClose,
    popoverAction,
    applicableTutorials,
    allTutorials,
}) => {
    const {t} = useTranslation('components', {keyPrefix: 'panel-sections.app-bar.actions.biz-learn-videos'});
    const [tab, setTab] = useState<TutorialsTab>('applicable');
    const [searchQuery, setSearchQuery] = useState('');
    const scrollRef = useRef<HTMLDivElement | null>(null);

    /**
     * Resets search, tab, and list scroll when the popover closes.
     */
    useEffect(() => {
        if (open) {
            return;
        }
        setSearchQuery('');
        setTab('applicable');
        if (scrollRef.current) {
            scrollRef.current.scrollTop = 0;
        }
    }, [open]);

    /**
     * Resets list scroll when the active tab or search query changes.
     */
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = 0;
        }
    }, [tab, searchQuery]);

    const sourceTutorials = tab === 'applicable' ? applicableTutorials : allTutorials;

    /**
     * Filters the active tab's videos by the live search query (case-insensitive title match).
     */
    const filteredTutorials = useMemo(() => {
        const normalized = searchQuery.trim().toLowerCase();
        if (!normalized) {
            return sourceTutorials;
        }
        return sourceTutorials.filter((tutorial) => tutorial.title.toLowerCase().includes(normalized));
    }, [searchQuery, sourceTutorials]);

    const getScrollParent = useCallback(() => (open ? scrollRef.current : null), [open]);

    const useVirtual = filteredTutorials.length > VIRTUALIZE_THRESHOLD;

    const virtualizer = useFixedRowVirtualizer({
        data: filteredTutorials,
        rowHeight: VIDEO_ROW_HEIGHT,
        overscan: 4,
        getScrollParent,
        getItemKey: (item) => item.url,
    });

    /**
     * Clears the search field.
     */
    const clearSearch = useCallback(() => {
        setSearchQuery('');
    }, []);

    /**
     * Updates the active tutorials tab.
     */
    const handleTabChange = useCallback((_: React.SyntheticEvent, value: TutorialsTab) => {
        setTab(value);
    }, []);

    const emptyMessage = searchQuery.trim()
        ? t($ => $.noSearchResults)
        : t($ => $.noVideos);

    return (
        <Popover
            id="biz-learn-videos-popover"
            action={popoverAction}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
            }}
            slotProps={{
                root: {sx: {marginBlockStart: 2}},
                paper: {
                    sx: (theme) => ({
                        boxShadow: theme.shadows[2],
                        width: POPOVER_WIDTH,
                        maxWidth: 'min(100vw - 24px, 420px)',
                    }),
                },
            }}
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                    p: 2,
                    pb: 1.5,
                }}
            >
                <Typography variant="body1" fontWeight={600}>
                    {t($ => $.title, {count: 2})}
                </Typography>

                <Tabs
                    value={tab}
                    onChange={handleTabChange}
                    variant="fullWidth"
                    sx={{
                        minHeight: 36,
                        '& .MuiTab-root': {
                            minHeight: 36,
                            py: 0.5,
                            textTransform: 'none',
                        },
                    }}
                >
                    <Tab
                        value="applicable"
                        label={t($ => $.tabs.applicable, {count: applicableTutorials.length})}
                    />
                    <Tab
                        value="all"
                        label={t($ => $.tabs.all, {count: allTutorials.length})}
                    />
                </Tabs>

                <TextField
                    size="small"
                    fullWidth
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={t($ => $.searchPlaceholder)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchRounded fontSize="small" color="action"/>
                            </InputAdornment>
                        ),
                        endAdornment: searchQuery
                            ? (
                                <InputAdornment position="end">
                                    <IconButton
                                        size="small"
                                        aria-label={t($ => $.clearSearch)}
                                        onClick={clearSearch}
                                        edge="end"
                                    >
                                        <ClearRounded fontSize="small"/>
                                    </IconButton>
                                </InputAdornment>
                            )
                            : undefined,
                    }}
                />

                {
                    !filteredTutorials.length &&
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        textAlign="center"
                        sx={{py: 4, px: 1}}
                    >
                        {emptyMessage}
                    </Typography>
                }

                {
                    !!filteredTutorials.length &&
                    <Box
                        ref={scrollRef}
                        sx={{
                            // Fixed height when virtualizing so the scrollport has a measurable
                            // clientHeight on attach (maxHeight alone collapses to content).
                            height: useVirtual ? LIST_MAX_HEIGHT : 'auto',
                            maxHeight: LIST_MAX_HEIGHT,
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            mx: -1,
                        }}
                    >
                        {
                            useVirtual
                                ? (
                                    <>
                                        <Box sx={{height: virtualizer.topSpacerHeight}}/>
                                        {virtualizer.items.map((tutorial, visibleIndex) => (
                                            <VideoRow
                                                key={virtualizer.keyFor(visibleIndex)}
                                                tutorial={tutorial}
                                                searchQuery={searchQuery}
                                            />
                                        ))}
                                        <Box sx={{height: virtualizer.bottomSpacerHeight}}/>
                                    </>
                                )
                                : filteredTutorials.map((tutorial) => (
                                    <VideoRow
                                        key={tutorial.url}
                                        tutorial={tutorial}
                                        searchQuery={searchQuery}
                                    />
                                ))
                        }
                    </Box>
                }
            </Box>
        </Popover>
    );
};

export default TutorialsPopover;
